/**
 * Centralized page/feature access-check helpers.
 *
 * Two comparable "completeness statuses" a page/nav item/settings entry can carry:
 * - "demo": Orderly-internal only. Gated by the current user's email domain. NOT signposted
 *   in the UI (no badge/chip) — it's purely a visibility gate for Orderly staff.
 * - "preview": Customer-visible. Gated by environment (test/qa/uat/localhost) or the Admin
 *   permission. Always signposted with a "Preview" badge/chip wherever it's shown.
 *
 * Both Settings (src/pages/Settings/index.tsx) and the nav drawer
 * (src/components/ui/drawer/**) call the same `canAccessPageStatus` helper so new pages of
 * varying completeness are gated consistently in both places.
 */

export type PageAccessStatus = 'demo' | 'preview';

/* ---- Demo (Orderly-internal, opt-in, not signposted) ---- */

const DEMO_MODE_STORAGE_KEY = 'msw-enabled';

/** Email domains allowed to see/use Demo Mode. */
const DEMO_ALLOWED_DOMAINS = ['orderly.io', 'kudosqa.com'];

/**
 * Returns true if the given email/hostname combination is allowed to opt into Demo Mode.
 */
export function isDemoModeEligible(email: string, hostname: string): boolean {
  if (!email) return false;
  const normalizedHostname = hostname.toLowerCase();
  const environmentEligible = normalizedHostname === 'localhost'|| normalizedHostname.includes('demo') || normalizedHostname.includes('uat');
  const domain = email.split('@')[1]?.toLowerCase();
  return environmentEligible && DEMO_ALLOWED_DOMAINS.includes(domain);
}

/** Returns true if the user has manually opted into Demo Mode. */
export function isDemoModeEnabled(): boolean {
  return localStorage.getItem(DEMO_MODE_STORAGE_KEY) === 'true';
}

/** Enables Demo Mode (persists to localStorage). Call after successful login when opted in. */
export function enableDemoMode(): void {
  localStorage.setItem(DEMO_MODE_STORAGE_KEY, 'true');
}

/** Disables Demo Mode (removes from localStorage). Call on logout. */
export function disableDemoMode(): void {
  localStorage.removeItem(DEMO_MODE_STORAGE_KEY);
}

/* ---- Preview (customer-visible, environment/permission driven, signposted) ---- */

const PREVIEW_ALLOWED_HOSTNAMES = ['localhost', '127.0.0.1', '::1'];
const PREVIEW_ALLOWED_HOSTNAME_FRAGMENTS = ['test', 'qa', 'uat'];

/** Returns true if the given hostname is a non-production environment eligible for Preview. */
export function isPreviewEligibleHostname(hostname: string): boolean {
  const normalizedHostname = hostname.toLowerCase();
  return PREVIEW_ALLOWED_HOSTNAMES.includes(normalizedHostname)
    || PREVIEW_ALLOWED_HOSTNAME_FRAGMENTS.some((fragment) => normalizedHostname.includes(fragment));
}

/**
 * Returns true if Preview content should be visible: either the user is an Admin (any
 * environment), or the current environment is a non-production one.
 */
export function isPreviewEligible(hostname: string | undefined, hasAdminPermission: boolean): boolean {
  if (hasAdminPermission) return true;
  if (!hostname) return false;
  return isPreviewEligibleHostname(hostname);
}

/* ---- Demo-only permission bypass (fixes internal page-level permission checks) ---- */

/**
 * Permission-name prefixes that belong exclusively to demo-flagged domains (no real backend).
 * Pages under these domains call `hasPermissionTo(...)` internally (separately from the
 * route/nav-level `status: 'demo'` bypass) to gate buttons/tabs/detail views — this list lets
 * `hasPermissionTo` (see useUser.tsx) bypass those checks too when Demo Mode is enabled, without
 * weakening permission checks for any real/production permission.
 */
const DEMO_ONLY_PERMISSION_PREFIXES = [
  'CashManagement',
  'CashReconciliation',
  'OperationalDays',
  'TaskSchedules',
  'TaskTemplates',
  'TaskCategories',
  'AdvancedTasks',
  'AutomatedWorkflows',
  'Documents',
  'RecipeV2',
  'RangePlanningItems',
  'Menus',
  'AttributesAllergens',
  'RangeOverview',
  'RangeDevelopment',
  'ReferenceData',
  'ItemRationalisation',
  'NewsManagement',
  'FinanceDashboard',
  'FinanceProfitAndLoss',
  'Credits',
  'LabourManagement',
  'EstateManagement',
  'Sustainability',
  'StoreDevelopment',
  'StoreZones',
  'EquipmentManagement',
  'SiteManagement',
  'EquipmentCatalogue',
  'ServiceRequests',
  'MaintenanceCompliance',
  'MySite',
  'SiteOperations',
  'SupplierEsgDashboard',
  'WasteManagementDashboard',
];

/** Returns true if the given permission name belongs exclusively to a demo-flagged domain. */
export function isDemoOnlyPermission(permission: string): boolean {
  return DEMO_ONLY_PERMISSION_PREFIXES.some((prefix) => permission.startsWith(prefix));
}

/* ---- Demo-mode bolted-on permissions + role picker (real permissions, narrowed by persona) ---- */

/**
 * Real permissions (backed by actual `auth.Permissions` rows in IM) for the procurement
 * demo's role-based approval chain. Unlike `DEMO_ONLY_PERMISSION_PREFIXES`, these are meant
 * to be properly assigned to real roles/personas in UAT for genuine multi-login RBAC
 * testing. When Demo Mode is enabled they're also "bolted on" to whoever is logged in -
 * added to their real held permissions - so a single demo account can walk the whole
 * approval chain without needing five separate logins. This is an explicit exact-name list
 * (not a prefix match) so it can't accidentally widen to catch unrelated future permissions.
 *
 * The demo Role Picker (see `DemoPersona` below) then NARROWS this bolted-on set (and the
 * user's real held permissions) down to whichever persona is selected, so the single demo
 * account can also simulate what a lower-privileged persona would see.
 */
const DEMO_BOLT_ON_PERMISSIONS = [
  'SupplierGovernanceApprove',
  'ComplianceAllergenApprove',
  'CommercialMarginApprove',
  'OrderBudgetExceptionApprove',
  'ProcurementRolloutApprove',
];

/** Returns true if the given permission is bolted on to the logged-in user while Demo Mode is enabled. */
export function isDemoBoltOnPermission(permission: string): boolean {
  return DEMO_BOLT_ON_PERMISSIONS.includes(permission);
}

/** Personas selectable via the demo Role Picker. `null` means "no persona selected" -
 * unrestricted, i.e. the full held permission set (real + demo bolt-on) applies as-is. */
export type DemoPersona = 'Chef' | 'Procurement' | 'Compliance' | 'Commercial' | 'HeadOffice' | 'Operations';

export const DEMO_PERSONAS: { value: DemoPersona; label: string }[] = [
  { value: 'Chef', label: 'Chef' },
  { value: 'Procurement', label: 'Procurement' },
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'HeadOffice', label: 'Head Office' },
  { value: 'Operations', label: 'Operations' },
];

/**
 * Curated (not exhaustive) permission-name prefixes each demo persona needs to reach their
 * part of the procurement demo journey - narrows the picker to a believable subset rather
 * than modelling a complete real-world RBAC matrix.
 */
const DEMO_PERSONA_PERMISSION_PREFIXES: Record<DemoPersona, string[]> = {
  Chef: ['RecipeV2', 'Recipe', 'Item', 'Product', 'Conversion'],
  Procurement: ['Supplier', 'Catalogue', 'Category', 'OrderType', 'Instance', 'Market', 'Product'],
  Compliance: ['RecipeV2', 'Audit', 'Training', 'Compliance', 'JobRoles', 'ActionItems'],
  Commercial: ['RecipeV2', 'Report', 'Product'],
  HeadOffice: ['Order', 'Report', 'Finance', 'Product', 'EstateManagement', 'Sustainability', 'StoreDevelopment', 'StoreZones', 'EquipmentManagement', 'SiteManagement', 'EquipmentCatalogue', 'ServiceRequests', 'MaintenanceCompliance', 'MySite', 'SiteOperations'],
  // 'Finance' and 'ViewCompliance' added so the Operational Day Hub (built for this persona)
  // is self-consistent - it links to Finance Dashboard and Compliance Dashboard cards, both
  // of which would otherwise be filtered out for Operations under narrowed permissions.
  Operations: ['OperationalDays', 'AdvancedTasks', 'TaskTemplates', 'TaskSchedules', 'TaskCategories', 'StockCount', 'Stock', 'Transfer', 'DailyWasteCheckSheet', 'Product', 'PurchaseOrder', 'Finance', 'ViewCompliance', 'LabourManagement', 'WasteManagementDashboard'],
};

/** The one demo bolt-on approval permission each persona is meant to exercise. */
const DEMO_PERSONA_APPROVAL_PERMISSION: Record<DemoPersona, string | undefined> = {
  Chef: undefined,
  Procurement: 'SupplierGovernanceApprove',
  Compliance: 'ComplianceAllergenApprove',
  Commercial: 'CommercialMarginApprove',
  HeadOffice: 'OrderBudgetExceptionApprove',
  Operations: 'ProcurementRolloutApprove',
};

/**
 * Returns true if `permission` is within the selected persona's narrowed scope. Returns
 * true unconditionally when no persona is selected (`persona` is undefined/null) - the
 * picker can only ever REDUCE permissions the user already holds, never grant new ones.
 */
export function isPermissionAllowedForPersona(permission: string, persona: DemoPersona | null | undefined): boolean {
  if (!persona) return true;
  if (permission === DEMO_PERSONA_APPROVAL_PERMISSION[persona]) return true;
  return DEMO_PERSONA_PERMISSION_PREFIXES[persona].some((prefix) => permission.startsWith(prefix));
}

/**
 * Personas whose day-to-day work is scoped to a single store (Chef/Operations run a specific
 * site). All other personas (Procurement/Compliance/Commercial/Head Office) operate across the
 * estate rather than one store, so store-scoped UI (e.g. the DashboardV2 "Store" task filter)
 * should default to off for them.
 */
const STORE_SPECIFIC_PERSONAS: DemoPersona[] = ['Chef', 'Operations'];

/** Returns true if the given persona's work is scoped to a single store. `null`/undefined
 * (no persona selected, i.e. "All") is treated as store-specific - the unrestricted default. */
export function isStoreSpecificPersona(persona: DemoPersona | null | undefined): boolean {
  if (!persona) return true;
  return STORE_SPECIFIC_PERSONAS.includes(persona);
}

/* ---- Unified dispatcher used by Settings + Drawer ---- */

export interface PageAccessContext {
  hostname?: string;
  hasAdminPermission?: boolean;
}

/**
 * Single entry point for gating a page/nav item/settings entry by its completeness status.
 * Returns true (visible) when status is undefined — i.e. fully released pages are unaffected.
 * Demo Mode also grants access to 'preview' content (Demo Mode is a superset) — see
 * `shouldShowPreviewTag` for the separate decision of whether to still show the "Preview" badge.
 */
export function canAccessPageStatus(status: PageAccessStatus | undefined, context: PageAccessContext): boolean {
  if (!status) return true;
  if (status === 'demo') return isDemoModeEnabled();
  return isDemoModeEnabled() || isPreviewEligible(context.hostname, context.hasAdminPermission ?? false);
}

/**
 * Returns true if the "Preview" badge/chip should be shown for a preview-flagged item. Preview
 * screens remain visible while Demo Mode is enabled (see `canAccessPageStatus`), but the tag
 * itself should be suppressed in that case — Demo Mode is internal-only and shouldn't surface a
 * customer-facing "Preview" label.
 */
export function shouldShowPreviewTag(status: PageAccessStatus | undefined): boolean {
  return status === 'preview' && !isDemoModeEnabled();
}

/**
 * Inverse of `canAccessPageStatus`: some pages/items should be HIDDEN while Demo Mode is
 * enabled (e.g. a real page that's superseded by a demo equivalent, or a preview section that
 * would be confusing to show alongside demo content). Use the `hideInDemo` flag on a
 * `DrawerNavItem`/`SettingItem`/`SettingSection` for this — it takes priority over any other
 * visibility check.
 */
export function shouldHideInDemo(hideInDemo: boolean | undefined): boolean {
  return !!hideInDemo && isDemoModeEnabled();
}
